/**
 * Suite app normalization utilities.
 *
 * Central's switch-context response may use `appId` or `id`.
 * These helpers normalize the raw payload, filter hidden apps,
 * sort by `order`, and inject the current app if Central omits it.
 */

import type { SuiteApp, NormalizedSuiteApp } from '../config/centralAuth';

/**
 * Normalize raw suite apps from Central:
 * - Use `appId` as the guaranteed `id`
 * - Map `name` to `displayName` for UI display
 * - Filter out apps with `status === 'hidden'`
 * - Sort by `order` ascending (missing order defaults to 999)
 */
export function normalizeApps(raw: SuiteApp[] | undefined): NormalizedSuiteApp[] {
  if (!raw || raw.length === 0) return [];
  return raw
    .filter((app) => app.appId)
    .map((app) => ({
      id: app.appId,
      displayName: app.name,
      webUrl: app.webUrl,
      iconUrl: app.iconUrl,
      mobileScheme: app.mobileScheme,
      status: app.status,
      order: app.order ?? 999,
    }))
    .filter((app) => app.status !== 'hidden')
    .sort((a, b) => a.order - b.order);
}

/**
 * Ensure the current app is present in the list.
 * If Central omits it, inject a fallback entry at the top.
 */
export function ensureCurrentApp(
  apps: NormalizedSuiteApp[],
  currentAppId: string,
  currentDisplayName: string,
  currentWebUrl: string,
): NormalizedSuiteApp[] {
  if (apps.some((a) => a.id === currentAppId)) return apps;
  return [
    {
      id: currentAppId,
      displayName: currentDisplayName,
      webUrl: currentWebUrl,
      status: 'active',
      order: -999,
    },
    ...apps,
  ];
}
